import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { getOfflineModule, gradeLocally, isNetworkError, queueSubmit } from "@/lib/offline";
import { track } from "@/lib/track";
import { OfflineBanner } from "@/components/OfflineControls";
import { QuestionRunner } from "@/components/QuestionRunner";
import { Confetti } from "@/components/Confetti";
import { useResumableSession, ResumePrompt } from "@/components/ResumeSession";
import { Mascot } from "@/components/Mascot";
import { ScoreCard, Breakdown, ReviewList } from "@/components/ResultView";
import { usePrefs } from "@/context/PrefsContext";
import { api, errMsg } from "@/lib/api";

export function TestPage() {
  const { moduleId } = useParams();
  const { t } = usePrefs();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const loader = useCallback(() => api.get(`/modules/${moduleId}/test`).then((r) => r.data).catch(async (e) => { if (!isNetworkError(e)) throw e; const o = await getOfflineModule(moduleId); if (!o?.test) throw e; setOffline(true); return o.test; }), [moduleId]);
  const ses = useResumableSession(`yk_test_${moduleId}`, loader);
  if (ses.saved) return <ResumePrompt saved={ses.saved} answered={ses.answered} onResume={ses.resume} onStartOver={ses.startOver} />;
  const data = ses.data;
  if (!data) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  const submit = async (answers) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/modules/${moduleId}/test/submit`, { answers });
      ses.clear();
      nav(`/test/result/${res.id}`, { state: { result: res, fresh: true }, replace: true });
    } catch (e) {
      if (isNetworkError(e)) {
        const o = await getOfflineModule(moduleId).catch(() => null);
        if (o?.test) { await queueSubmit(moduleId, answers); track("offline_test", { module_id: moduleId }); const res = gradeLocally({ ...o.test, module_id: moduleId }, answers); ses.clear(); nav(`/test/result/${res.id}`, { state: { result: res, fresh: true, offline: true }, replace: true }); return; }
      }
      toast.error(errMsg(e)); setBusy(false);
    }
  };
  const checkpoint = async (answers) => {
    try { return (await api.post("/review/checkpoint", { answers })).data; } catch (e) { toast.error(errMsg(e)); return null; }
  };
  return (
    <div data-testid="test-page">
      {offline && <div className="mx-auto mb-4 max-w-2xl"><OfflineBanner synced={false} /></div>}
      <p className="mx-auto mb-6 max-w-2xl text-xs uppercase tracking-wide text-muted-foreground">{data.batch_size ? t("evaluation") : t("miniTest")} · {data.title_id}{data.batch_size ? ` · ${data.total} ${t("question").toLowerCase()}` : ""}</p>
      <QuestionRunner key={`${moduleId}-${data.questions[0]?.id}`} questions={data.questions} onSubmit={submit} submitting={busy} batchSize={data.batch_size} onCheckpoint={checkpoint} instant={data.instant} initialAnswers={ses.initialAnswers} initialIdx={ses.initialIdx} onProgress={ses.onProgress} />
    </div>
  );
}

export function TestResult() {
  const { attemptId } = useParams();
  const { t } = usePrefs();
  const { state } = useLocation();
  const [res, setRes] = useState(state?.result || null);
  useEffect(() => { if (!res && !attemptId.startsWith("local-")) api.get(`/tests/${attemptId}`).then((r) => setRes(r.data)).catch(() => {}); }, [attemptId, res]);
  if (!res) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="test-result-page">
      {res.offline && <OfflineBanner synced={false} />}
      <Confetti show={Boolean(state?.fresh && res.passed)} />
      {res.mascot && <div className="soft-card p-5 fade-up"><Mascot m={res.mascot} size={110} testid="test-result-mascot" /></div>}
      <ScoreCard result={res} title={`${t("testResultTitle")} · ${res.module_title}`}>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <span data-testid="test-passed-badge" className={`rounded-full px-3 py-1 text-xs font-semibold ${res.passed ? "bg-sage-soft text-sage" : "bg-primary/10 text-primary"}`}>{res.passed ? t("passed") : t("notPassed")}{res.auto_completed ? ` · ${t("autoCompleted")}` : ""}</span>
          <div className="flex gap-2">
            <Link data-testid="test-result-module-link" to={`/modules/${res.module_id}`} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-accent">{t("toModule")}</Link>
            <Link data-testid="test-result-retry-link" to={`/test/${res.module_id}`} className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-accent">{t("retry")}</Link>
            <Link data-testid="test-result-dashboard-link" to="/dashboard" className="rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background">{t("backDash")}</Link>
          </div>
        </div>
      </ScoreCard>
      {res.breakdown?.length > 1 && <Breakdown breakdown={res.breakdown} />}
      <ReviewList items={res.items} />
    </div>
  );
}
